/**
 * Client-safe broker / Trading Server labels.
 * Prefer API-provided values; never show MT5 Manager host/IP/port in the UI.
 */

const ENV_BROKER =
  (typeof import.meta !== 'undefined' &&
    (import.meta as ImportMeta & { env?: Record<string, string> }).env
      ?.VITE_BROKER_DISPLAY_NAME) ||
  ''

export const BROKER_DISPLAY_NAME = (ENV_BROKER || 'iBull Capital').trim() || 'iBull Capital'

const HOST_PORT_RE =
  /^(?:\d{1,3}\.){3}\d{1,3}(?::\d+)?$|^[a-z0-9.-]+:\d+$/i

export function looksLikeRawMt5Host(value?: string | null): boolean {
  const text = String(value || '').trim()
  if (!text) return false
  if (HOST_PORT_RE.test(text)) return true
  if (/^\d{1,3}(?:\.\d{1,3}){3}/.test(text)) return true
  return false
}

/** e.g. "iBull Capital - Demo" / "iBull Capital - Live" */
export function tradingServerLabel(
  mode?: string | null,
  apiServer?: string | null,
): string {
  const normalized = String(mode || 'DEMO').trim().toUpperCase()
  const suffix = normalized === 'LIVE' || normalized === 'REAL' ? 'Live' : 'Demo'
  const derived = `${BROKER_DISPLAY_NAME} - ${suffix}`
  const fromApi = String(apiServer || '').trim()
  if (!fromApi || looksLikeRawMt5Host(fromApi)) return derived
  // Accept API-safe labels that already match the broker pattern.
  if (/ - (Demo|Live)$/i.test(fromApi)) return fromApi
  // Any other opaque host-like string → use derived client-safe label.
  if (/[:/]/.test(fromApi) || /\d+\.\d+\.\d+\.\d+/.test(fromApi)) return derived
  return derived
}

export type ConnectionPresentation = {
  key: 'connected' | 'updating' | 'delayed' | 'unavailable' | 'offline'
  label: string
  tone: 'ok' | 'warn' | 'danger' | 'muted'
}

/** Map internal sync_state to client-safe connection wording. */
export function connectionStatus(
  syncState?: string | null,
  opts?: { refreshing?: boolean },
): ConnectionPresentation {
  if (opts?.refreshing) {
    return { key: 'updating', label: 'Updating', tone: 'warn' }
  }
  const raw = String(syncState || '').trim().toUpperCase()
  if (!raw || raw === 'UNKNOWN') {
    return { key: 'unavailable', label: 'Temporarily unavailable', tone: 'muted' }
  }
  if (raw === 'LIVE' || raw === 'FRESH' || raw === 'CONNECTED' || raw === 'OK') {
    return { key: 'connected', label: 'Connected', tone: 'ok' }
  }
  if (raw === 'REFRESHING' || raw === 'SYNCING' || raw === 'UPDATING') {
    return { key: 'updating', label: 'Updating', tone: 'warn' }
  }
  if (raw === 'STALE') {
    return { key: 'delayed', label: 'Last update delayed', tone: 'warn' }
  }
  if (raw === 'OFFLINE' || raw === 'DISCONNECTED') {
    return { key: 'offline', label: 'Connection unavailable', tone: 'danger' }
  }
  if (
    raw.includes('UNAVAILABLE') ||
    raw.includes('ERROR') ||
    raw.includes('FAIL') ||
    raw === 'NOTFOUND'
  ) {
    return { key: 'unavailable', label: 'Temporarily unavailable', tone: 'danger' }
  }
  return { key: 'unavailable', label: 'Temporarily unavailable', tone: 'muted' }
}

export function statusLabel(status?: string | null): string {
  const s = String(status || '').trim()
  if (!s) return 'Active'
  const upper = s.toUpperCase()
  if (upper === 'ACTIVE') return 'Active'
  if (upper === 'DISABLED') return 'Disabled'
  if (upper === 'ARCHIVED') return 'Archived'
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}
